import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../../components/BottomNav';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ArrowRight, Crown, Plus, QrCode, Lightning, Question, X, FileText, Download } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { sparklePoints } from '../../utils/confetti';
import { generateStatement } from '../../services/PDFExportService';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = {
  bronze: { name: 'Bronze', min: 0, color: '#CD7F32' },
  silver: { name: 'Silver', min: 500, color: '#C0C0C0' },
  gold: { name: 'Gold', min: 2000, color: '#C3A365' }
};

const getTier = (pts) => pts >= 2000 ? 'gold' : pts >= 500 ? 'silver' : 'bronze';

const Wallet = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRedeem, setShowRedeem] = useState(false);
  const [redeemPts, setRedeemPts] = useState('');
  const [showNewUserTip, setShowNewUserTip] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    fetchWallet();
  }, []);

  useEffect(() => {
    // Show tip for new users with 0 points
    if (wallet && wallet.points_balance === 0 && !localStorage.getItem('wallet-tip-dismissed')) {
      setShowNewUserTip(true);
    }
  }, [wallet]);

  const fetchWallet = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/consumer/wallet`, { withCredentials: true });
      setWallet(data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    const pts = parseInt(redeemPts);
    if (!pts || pts < 100) {
      toast.error('Minimum 100 points');
      return;
    }
    try {
      const { data } = await axios.post(`${API_URL}/api/consumer/redeem-points`, { points: pts }, { withCredentials: true });
      toast.success(`${pts} points convertis en £${data.credit_amount}`);
      sparklePoints();
      setShowRedeem(false);
      setRedeemPts('');
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec');
    }
  };

  const handleExportStatement = async () => {
    setExportingPdf(true);
    try {
      // Try to fetch transactions from API
      let transactions = [];
      try {
        const { data } = await axios.get(`${API_URL}/api/consumer/transactions`, { withCredentials: true });
        transactions = data.transactions || [];
      } catch (e) {
        // If API doesn't exist, use mock data for demo
        transactions = generateMockTransactions();
      }

      // Get current month name
      const now = new Date();
      const monthNames = language === 'fr' 
        ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const period = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;

      // Calculate summary
      const totalCredits = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
      const totalDebits = Math.abs(transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0));
      const pointsEarned = transactions.reduce((sum, t) => sum + (t.points_earned || 0), 0);

      generateStatement({
        user: {
          name: wallet?.user?.name || 'Membre habitué',
          id: wallet?.user?.id || '000000',
          email: wallet?.user?.email
        },
        transactions,
        period,
        summary: {
          openingBalance: (wallet?.card_balance || 0) - totalCredits + totalDebits,
          totalCredits,
          totalDebits,
          closingBalance: wallet?.card_balance || 0,
          pointsEarned: pointsEarned || wallet?.points_balance || 0
        },
        language
      });

      toast.success(language === 'fr' ? 'Relevé téléchargé !' : 'Statement downloaded!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'export' : 'Export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  // Generate mock transactions for demo
  const generateMockTransactions = () => {
    const types = ['topup', 'payment', 'gift_received', 'points_redeemed'];
    const merchants = ['Le Petit Café', 'Bistro Saint-Germain', 'Boulangerie Blanc', 'Salon Camille'];
    const transactions = [];
    const now = new Date();
    
    for (let i = 0; i < 8; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i * 3);
      const type = types[Math.floor(Math.random() * types.length)];
      const isCredit = type === 'topup' || type === 'gift_received' || type === 'points_redeemed';
      const amount = isCredit 
        ? Math.floor(Math.random() * 100) + 10 
        : -(Math.floor(Math.random() * 30) + 5);
      
      transactions.push({
        id: `tx_${i}`,
        date: date.toISOString(),
        type,
        description: type === 'payment' ? merchants[Math.floor(Math.random() * merchants.length)] : null,
        merchant_name: type === 'payment' ? merchants[Math.floor(Math.random() * merchants.length)] : null,
        amount,
        balance_after: 150 + (Math.random() * 100),
        points_earned: type === 'payment' ? Math.floor(Math.abs(amount) * 2) : 0
      });
    }
    
    return transactions.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const tier = wallet ? getTier(wallet.points_balance || 0) : 'bronze';
  const tierInfo = TIERS[tier];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#18261E] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#18261E] pb-32">
      {/* New User Tip Banner */}
      <AnimatePresence>
        {showNewUserTip && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gradient-to-r from-[#C3A365]/20 to-[#C3A365]/5 border-b border-[#C3A365]/30 px-5 py-4"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C3A365]/20 flex items-center justify-center flex-shrink-0">
                <Lightning size={20} className="text-[#C3A365]" weight="fill" />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm font-medium mb-1">
                  {language === 'fr' ? 'Bienvenue sur habitué !' : 'Welcome to habitué!'}
                </p>
                <p className="text-[#8BA89A] text-xs leading-relaxed mb-2">
                  {language === 'fr' 
                    ? 'Scannez votre premier QR code chez un marchand partenaire pour commencer à cumuler des points.'
                    : 'Scan your first QR code at a partner merchant to start earning points.'}
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/scanner')}
                    className="text-[#C3A365] text-xs font-medium flex items-center gap-1"
                  >
                    {language === 'fr' ? 'Scanner maintenant' : 'Scan now'} <ArrowRight size={12} />
                  </button>
                  <button
                    onClick={() => navigate('/how-it-works')}
                    className="text-[#8BA89A] text-xs flex items-center gap-1"
                  >
                    <Question size={12} /> {language === 'fr' ? 'Comment ça marche' : 'How it works'}
                  </button>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowNewUserTip(false);
                  localStorage.setItem('wallet-tip-dismissed', 'true');
                }}
                className="text-[#8BA89A] hover:text-white p-1"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="px-5 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[#C3A365] text-xs tracking-widest uppercase mb-1">
              {language === 'fr' ? 'Portefeuille' : 'Wallet'}
            </p>
            <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {wallet?.user?.name || (language === 'fr' ? 'Membre' : 'Member')}
            </h1>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{ backgroundColor: `${tierInfo.color}20`, border: `1px solid ${tierInfo.color}40` }}>
            <Crown size={14} style={{ color: tierInfo.color }} weight="fill" />
            <span className="text-xs font-medium" style={{ color: tierInfo.color }}>{tierInfo.name}</span>
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="px-5 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-[#1a1a1a] via-[#252525] to-[#1a1a1a] rounded-2xl p-5 relative overflow-hidden"
          style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C3A365]/50 to-transparent" />
          
          <div className="flex justify-between items-start mb-8">
            <span className="brand-logo text-xl text-[#E8EBE9]/80">habitué</span>
            <Crown size={20} style={{ color: tierInfo.color }} weight="fill" />
          </div>
          
          <div className="mb-4">
            <p className="text-[#888] text-xs uppercase tracking-wider mb-1">Points</p>
            <p className="text-3xl text-white font-light">{wallet?.points_balance || 0}</p>
            <p className="text-[#888] text-xs mt-1">≈ £{((wallet?.points_balance || 0) / 100).toFixed(2)}</p>
          </div>
          
          <div className="flex justify-between text-xs text-[#888]">
            <span>•••• {(wallet?.user?.id || '0000').slice(-4)}</span>
            <span>Membre 2025</span>
          </div>
        </motion.div>
      </div>

      {/* Actions */}
      <div className="px-5 mb-8">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <button
            onClick={() => navigate('/send-gift')}
            className="bg-[#C3A365] text-[#18261E] py-4 rounded-xl font-semibold flex items-center justify-center gap-2"
            data-testid="wallet-send-gift"
          >
            <Gift size={18} weight="bold" />
            {language === 'fr' ? 'Offrir' : 'Gift'}
          </button>
          <button
            onClick={() => setShowRedeem(true)}
            className="bg-[#1E2F26] text-white py-4 rounded-xl font-medium flex items-center justify-center gap-2 border border-[#3E5248]"
            data-testid="wallet-convert"
          >
            <Plus size={18} />
            {language === 'fr' ? 'Convertir' : 'Redeem'}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/topup')}
            className="bg-[#1E2F26] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 border border-[#3E5248] hover:border-[#C3A365]/50 transition-colors"
            data-testid="wallet-topup"
          >
            <Lightning size={16} weight="fill" className="text-[#C3A365]" />
            <span className="text-sm">{language === 'fr' ? 'Recharger' : 'Top Up'}</span>
          </button>
          <button
            onClick={() => navigate('/charge-card')}
            className="bg-[#1E2F26] text-white py-3 rounded-xl font-medium flex items-center justify-center gap-2 border border-[#3E5248] hover:border-[#C3A365]/50 transition-colors"
            data-testid="wallet-charge-card"
          >
            <QrCode size={16} className="text-[#C3A365]" />
            <span className="text-sm">Charge Card</span>
          </button>
        </div>
      </div>

      {/* Gift Cards */}
      <div className="px-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-medium">{language === 'fr' ? 'Mes cartes cadeaux' : 'My gift cards'}</h2>
          <span className="text-[#C3A365] text-sm">{wallet?.gift_cards?.length || 0}</span>
        </div>

        {(!wallet?.gift_cards || wallet.gift_cards.length === 0) ? (
          <div className="bg-[#1E2F26] rounded-xl p-8 text-center">
            <Gift size={32} className="mx-auto text-[#8BA89A] mb-3" weight="thin" />
            <p className="text-[#8BA89A] text-sm mb-4">{language === 'fr' ? 'Aucune carte cadeau' : 'No gift cards'}</p>
            <button
              onClick={() => navigate('/send-gift')}
              className="text-[#C3A365] text-sm font-medium flex items-center gap-1 mx-auto"
            >
              {language === 'fr' ? 'Offrir une carte' : 'Send a gift card'} <ArrowRight size={14} />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {wallet.gift_cards.map((card) => (
              <div key={card.id} className="bg-[#1E2F26] rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#283D32] flex items-center justify-center">
                    <Gift size={18} className="text-[#C3A365]" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{card.merchant_name || 'Universel'}</p>
                    <p className="text-[#8BA89A] text-xs">{card.code?.slice(-8)}</p>
                  </div>
                </div>
                <p className="text-[#C3A365] font-semibold">£{card.balance?.toFixed(2)}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Link */}
      <div className="px-5 mt-6">
        <button
          onClick={() => navigate('/how-it-works')}
          className="w-full bg-[#1E2F26] border border-[#283D32] rounded-xl p-4 flex items-center justify-between hover:border-[#3E5248] transition-colors"
          data-testid="help-link"
        >
          <div className="flex items-center gap-3">
            <Question size={20} className="text-[#8BA89A]" />
            <span className="text-[#8BA89A] text-sm">
              {language === 'fr' ? 'Besoin d\'aide ? Comment ça marche' : 'Need help? How it works'}
            </span>
          </div>
          <ArrowRight size={16} className="text-[#8BA89A]" />
        </button>
      </div>

      {/* Export Statement */}
      <div className="px-5 mt-3">
        <button
          onClick={handleExportStatement}
          disabled={exportingPdf}
          className="w-full bg-[#1E2F26] border border-[#283D32] rounded-xl p-4 flex items-center justify-between hover:border-[#C3A365]/50 transition-colors disabled:opacity-50"
          data-testid="export-statement-btn"
        >
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-[#C3A365]" />
            <span className="text-white text-sm">
              {language === 'fr' ? 'Télécharger mon relevé mensuel' : 'Download monthly statement'}
            </span>
          </div>
          {exportingPdf ? (
            <div className="w-4 h-4 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin" />
          ) : (
            <Download size={16} className="text-[#C3A365]" />
          )}
        </button>
      </div>

      {/* Redeem Modal */}
      {showRedeem && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          onClick={() => setShowRedeem(false)}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            className="absolute bottom-0 left-0 right-0 bg-[#1E2F26] rounded-t-3xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-[#3E5248] rounded-full mx-auto mb-6" />
            
            <h3 className="text-xl text-white font-medium mb-2">Convertir mes points</h3>
            <p className="text-[#8BA89A] text-sm mb-6">100 points = £1</p>
            
            <input
              type="number"
              value={redeemPts}
              onChange={(e) => setRedeemPts(e.target.value)}
              placeholder="Nombre de points"
              className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-4 text-white placeholder:text-[#8BA89A] mb-4 outline-none focus:border-[#C3A365]"
            />
            
            {redeemPts && (
              <p className="text-[#C3A365] text-sm mb-4">= £{(parseInt(redeemPts) / 100).toFixed(2)}</p>
            )}
            
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setShowRedeem(false)} className="py-4 rounded-xl text-white border border-[#3E5248]">
                Annuler
              </button>
              <button onClick={handleRedeem} className="py-4 rounded-xl bg-[#C3A365] text-[#18261E] font-semibold">
                Convertir
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <BottomNav />
    </div>
  );
};

export default Wallet;
